<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, nextTick, Ref } from "vue";
import { CursorValues, TextAreaKeyboardEvent, Direction, SnomedClinicalCode } from "./types";
import { Action } from "../consultation/MConsultationTopicHeading/types";
import { Key } from "ts-key-enum";
import { QItem } from "quasar";
import { useDebouncedThrottledFunction } from "../../../composables/throttleDebounce";
import axios from "../../../utils/axios";
import { searchArray } from "../../../utils/misc";
import { randomID } from "../../../utils/helpers";
import { makeHtmlIdSafe } from "../../../utils/strings";

const props = withDefaults(
  defineProps<{
    id: string;
    value?: string;
    actions?: Action[];
    disabled?: boolean;
    labelledBy: string;
    describeByText?: string;
  }>(),
  {
    describeByText:
      'Type to enter a note. Type "/" to open the actions menu or "." to code. Use arrow keys to navigate suggestions',
  },
);
const emit = defineEmits({
  "update:modelValue": (_id: string, _newValue: string, _immediate: boolean = false) => true,
  codeSelected: (_code: SnomedClinicalCode) => true,
  actionSelected: (_action: Action) => true,
  blur: (_inputValue: string) => true,
  enterKeydown: (_cursorValues: CursorValues) => true,
  backspaceAtStart: () => true,
  actionMenuChanged: (_isActionMenuShowing: boolean) => true,
});

const inputValue = ref<string>(props.value || "");
const inputEl = ref<InstanceType<typeof HTMLTextAreaElement> | null>(null);
const cloneEl = ref<InstanceType<typeof HTMLTextAreaElement> | null>(null);

// Actions menu
const isActionMenuShowing = ref<boolean>(false);
const filteredActions = ref<Action[]>(props.actions ?? []);
const refActionMenuItems = ref<QItem[]>([]);
const focusedActionIndex = ref(0);
const MENU_WIDTH = 300;
const MENU_MAX_HEIGHT = 300;
const ACTIONS_MENU_INIT_CHAR = "/";

// Clinical code menu
const isClinicalCodeMenuShowing = ref<boolean>(false);
const isCodeMenuSearching = ref<boolean>(false);
const refCodeMenuItems = ref(<QItem[]>[]);
const focusedCodeIndex = ref(0);
const codeSearchResults = ref<SnomedClinicalCode[]>([]);
const codeSearchAbortController = ref<AbortController>(new AbortController());
const {
  debouncedThrottled: searchCodesDebouncedThrottled,
  cancelDebouncedThrottled: cancelSearchCodesDebouncedThrottled,
} = useDebouncedThrottledFunction((searchText: string) => searchCodes(searchText), 1000, 1000);
const CODE_SEARCH_URL =
  "/clinical/gb/snomed/search/description/constrained?constrainingParentConcepts=71388002,404684003,243796009,48176007,272379006,363787002&outputParentConceptIds=1&excludeConstrainingConcepts=773051000000102,773031000000109,773011000000101,777441000000102,417753008,416308001,417370002,413909005";
const CODE_MENU_INIT_CHAR = ".";

defineExpose({ props, focus });

/**
 * Emits the update:modelValue event with the ID from props and the latest input value.
 * Helper functiion to reduce code repetition.
 * @param saveImmediately Whether the listener should cancel their debounce/throttle and instead save immediately
 */
function emitInputValue(saveImmediately: boolean = false) {
  emit("update:modelValue", props.id, inputValue.value, saveImmediately);
}

/**
 * Watches for input value change and debounces/throttles the emit so that the heading component
 * does not get overloaded with events when the user is typing.
 */

// can only be 1, visible & aria selected : true
const focusedItemId = ref("");

function getFocusedOrSelectedItemId() {
  const el =
    document.querySelector(".q-item:focus") ||
    document.querySelector(".q-item.q-item--focused") ||
    document.querySelector('.q-item[aria-selected="true"]') ||
    document.querySelector('.q-item[tabindex="0"]');

  return el?.id || null;
}

watch(
  inputValue,
  async (newValue, prevValue) => {
    // Update menu visibility
    isActionMenuShowing.value =
      newValue?.trim().startsWith(ACTIONS_MENU_INIT_CHAR) &&
      props.actions !== undefined &&
      props.actions?.length > 0;
    isClinicalCodeMenuShowing.value = newValue.trim().startsWith(CODE_MENU_INIT_CHAR);

    await nextTick();
    focusedItemId.value = getFocusedOrSelectedItemId();

    emit("actionMenuChanged", isActionMenuShowing.value);

    if (isActionMenuShowing.value && props.actions) {
      // Filter actions list with the latest text value (minus the forward slash)
      const searchText = newValue.slice(1).toLowerCase();
      focusedActionIndex.value = 0;

      if (searchText.length === 0) {
        filteredActions.value = props.actions;
      } else {
        filteredActions.value = searchArray(props.actions, searchText, "label");
      }
    } else if (isClinicalCodeMenuShowing.value) {
      // Search clinical codes
      focusedCodeIndex.value = 0;
      const searchText = newValue.slice(1).toLowerCase();

      if (searchText.length === 0) {
        if (prevValue?.startsWith(CODE_MENU_INIT_CHAR)) {
          cancelSearchCodesDebouncedThrottled();
          isCodeMenuSearching.value = false;
          codeSearchResults.value = [];
        }
      } else {
        isCodeMenuSearching.value = true;
        searchCodesDebouncedThrottled(searchText);
      }
    } else {
      const comingFromMenu =
        prevValue?.trim().startsWith(ACTIONS_MENU_INIT_CHAR) ||
        prevValue?.trim().startsWith(CODE_MENU_INIT_CHAR);

      // Emit new value to parent
      if (comingFromMenu === false) emitInputValue();
    }

    // New value could overflow to next line
    adjustHeight();
  },
  { immediate: true },
);

async function searchCodes(searchText: string) {
  // ENG-15066 when < is on a new line causes axios error
  codeSearchResults.value = await axios
    .get<{ results: SnomedClinicalCode[] }>(
      `${CODE_SEARCH_URL}&query=${searchText.replace(/</g, "%3C")}`,
      {
        signal: codeSearchAbortController.value.signal,
      },
    )
    .then((res) => {
      isCodeMenuSearching.value = false;

      return res.data.results;
    });
}
watch(isCodeMenuSearching, async (n) => {
  // when menu has returned filtered results...
  if (!n) {
    await nextTick();
    focusedItemId.value = `id-${makeHtmlIdSafe(codeSearchResults.value[0]?.label)}`;
  }
});

/**
 * If parent updates value, then update internal inputValue
 */
watch(
  () => props.value,
  (newValue) => {
    if (newValue) {
      inputValue.value = newValue;
      adjustHeight();
    }
  },
);

/**
 * Whenever the input loses focus, if the input value is different to the model value,
 * cancel any debounced/throttled callbacks
 * and emit the new value immediately with save immediately set to true
 */
function handleBlur(_event: FocusEvent & { target: HTMLTextAreaElement }) {
  emit("blur", inputValue.value);
}

function handleBackspace(event: TextAreaKeyboardEvent) {
  const selectionStart = event.target.selectionStart;
  const selectionEnd = event.target.selectionEnd;

  if (selectionStart === 0 && selectionEnd === 0) {
    event.preventDefault();
    emit("backspaceAtStart");
  }
}

function onEnterKeydown(event: TextAreaKeyboardEvent) {
  event.preventDefault();

  if (isActionMenuShowing.value === true) {
    if (filteredActions.value[focusedActionIndex.value]) {
      selectAction(filteredActions.value[focusedActionIndex.value]);
      focusedItemId.value = getFocusedOrSelectedItemId();
    }
    return;
  }

  if (isClinicalCodeMenuShowing.value === true) {
    if (codeSearchResults.value[focusedCodeIndex.value]) {
      selectCode(codeSearchResults.value[focusedCodeIndex.value]);
    }
    return;
  }

  // Get values before and after the current cursor selection position
  const selectionStart = event.target.selectionStart;
  const selectionEnd = event.target.selectionEnd;

  let leftOfSelection = null;
  if (selectionStart !== 0) leftOfSelection = inputValue.value.slice(0, selectionStart);

  let rightOfSelection = null;
  if (selectionEnd !== event.target.value.length)
    rightOfSelection = inputValue.value.slice(selectionEnd);

  emit("enterKeydown", <CursorValues>{
    leftOfSelection,
    rightOfSelection,
  });
}

function moveMenuFocus(direction: Direction, focusIndex: Ref, refList: Ref<QItem[]>) {
  if (direction === Direction.UP) {
    if (focusIndex.value - 1 >= 0) {
      focusIndex.value = focusIndex.value - 1;
    } else {
      focusIndex.value = refList.value.length - 1;
    }
  }

  if (direction === Direction.DOWN) {
    if (focusIndex.value + 1 <= refList.value.length - 1) {
      focusIndex.value = focusIndex.value + 1;
    } else {
      focusIndex.value = 0;
    }
  }
  nextTick(() => {
    const refFocusedItem = refList.value[focusIndex.value];
    refFocusedItem.$el.scrollIntoView();
  });
}

function handleKeydown(event: TextAreaKeyboardEvent) {
  switch (event.key) {
    case Key.ArrowDown: {
      event.preventDefault();
      if (isActionMenuShowing.value && refActionMenuItems.value.length > 0) {
        moveMenuFocus(Direction.DOWN, focusedActionIndex, refActionMenuItems);
      }

      if (isClinicalCodeMenuShowing.value && refCodeMenuItems.value.length > 0) {
        moveMenuFocus(Direction.DOWN, focusedCodeIndex, refCodeMenuItems);
      }

      break;
    }
    case Key.ArrowUp: {
      event.preventDefault();
      if (isActionMenuShowing.value && refActionMenuItems.value.length > 0) {
        moveMenuFocus(Direction.UP, focusedActionIndex, refActionMenuItems);
      }

      if (isClinicalCodeMenuShowing.value && refCodeMenuItems.value.length > 0) {
        moveMenuFocus(Direction.UP, focusedCodeIndex, refCodeMenuItems);
      }

      break;
    }
    case Key.Escape: {
      event.preventDefault();
      cancelSearchCodesDebouncedThrottled();
      inputValue.value = "";
      break;
    }
    case Key.Enter: {
      break;
    }
    default: {
      focusedActionIndex.value = 0;
    }
  }
}

function handlePaste(event: ClipboardEvent) {
  event.preventDefault(); // stop the browser inserting

  const pasted = event.clipboardData?.getData("text/plain") ?? "";

  if (pasted.length === 0) return;
  const newValue = pasted.replace(/^[\s.]+/, "");
  if (!newValue) return;

  const el = event.currentTarget as HTMLTextAreaElement;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? start;

  // insert transformed text at caret position
  el.setRangeText(newValue, start, end, "end");

  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function adjustHeight() {
  requestAnimationFrame(() => {
    if (inputEl.value && cloneEl.value) {
      // ENG-15011, clone added to stop jump caused by auto height
      cloneEl.value.value = inputEl.value.value;
      cloneEl.value.style.width = inputEl.value.offsetWidth + "px";
      inputEl.value.style.height = cloneEl.value.scrollHeight + "px";
    }
  });
}

onMounted(() => {
  adjustHeight();
  window.addEventListener("resize", adjustHeight);
});
onUnmounted(() => window.removeEventListener("resize", adjustHeight));

function focus(caretPosition: number | null = null) {
  if (inputEl.value) {
    inputEl.value?.focus();

    if (caretPosition !== null) {
      inputEl.value.setSelectionRange(caretPosition, caretPosition);
    }
  }
}

function selectAction(action: Action) {
  emit("actionSelected", action);
}

function selectCode(code: SnomedClinicalCode) {
  if (code) {
    emit("codeSelected", code);
  }
}

const id = randomID("mim");

watch(focusedActionIndex, (o) => {
  focusedItemId.value = `id-${makeHtmlIdSafe(filteredActions.value[o]?.label)}`;
});

watch(focusedCodeIndex, (o) => {
  focusedItemId.value = `id-${makeHtmlIdSafe(codeSearchResults.value[o]?.label)}`;
});
</script>

<template>
  <div class="consultation-entry-input">
    <span :id="`${id}-action-input-description`" class="sr-only">
      {{ describeByText }}
    </span>
    <textarea
      :id="`source_${id}`"
      ref="inputEl"
      v-model="inputValue"
      :disabled="disabled"
      rows="1"
      data-label="consultation-entry-textarea"
      :aria-labelledby="props.labelledBy"
      aria-haspopup="listbox"
      :aria-controls="id"
      aria-autocomplete="list"
      :aria-describedby="`${id}-action-input-description`"
      :aria-activedescendant="focusedItemId"
      @blur="handleBlur"
      @keydown.enter="onEnterKeydown"
      @keydown.backspace="handleBackspace"
      @keydown="handleKeydown"
      @paste="handlePaste"
    />
    <div class="overflow-hidden" style="position: relative">
      <!-- // ENG-15011, clone added to stop jump caused by auto height -->
      <textarea
        ref="cloneEl"
        v-model="inputValue"
        tabindex="-1"
        aria-hidden="true"
        disabled
        class="hidden-textarea"
      />
    </div>
    <m-menu
      v-if="isActionMenuShowing"
      :id="id"
      ref="actions-menu"
      class="m-action-menu"
      data-label="actions-menu"
      :target="inputEl"
      no-refocus
      no-focus
      persistent
      :model-value="true"
      :scroll-target="false"
      :max-height="MENU_MAX_HEIGHT"
      fit
      :style="{ width: MENU_WIDTH + 'px' }"
    >
      <q-list
        v-if="filteredActions.length > 0"
        :id="id"
        ref="list"
        :aria-labelledby="props.labelledBy"
        class="scroll-area"
        dense
        role="listbox"
      >
        <q-item
          v-for="(action, i) in filteredActions"
          :id="`id-${makeHtmlIdSafe(action.label)}`"
          :key="`${action.contextId}-${i}`"
          ref="refActionMenuItems"
          v-close-popup
          clickable
          :focused="i === focusedActionIndex"
          tabindex="-1"
          :aria-selected="i === focusedActionIndex"
          role="option"
          @click="() => selectAction(action)"
        >
          <q-item-section>
            <q-item-label>
              {{ action.label }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
      <div v-else class="label no-results-text">No results</div>
    </m-menu>
    <m-menu
      v-if="isClinicalCodeMenuShowing"
      ref="codes-menu"
      data-label="codes-menu"
      class="m-code-menu"
      :target="inputEl"
      no-refocus
      no-focus
      persistent
      :model-value="true"
      :scroll-target="false"
      :max-height="MENU_MAX_HEIGHT"
      :aria-labelledby="props.labelledBy"
      fit
      role="listbox"
    >
      <q-list
        v-if="isCodeMenuSearching === false && codeSearchResults.length > 0"
        :id="id"
        ref="list"
        class="code-menu-items scroll-area"
        dense
      >
        <q-item
          v-for="(code, i) in codeSearchResults"
          :id="`id-${makeHtmlIdSafe(code.label)}`"
          :key="`${code}-${i}`"
          ref="refCodeMenuItems"
          v-close-popup
          :data-id="`${code.id}-${i}`"
          clickable
          :focused="i === focusedCodeIndex"
          tabindex="-1"
          :aria-selected="i === focusedCodeIndex"
          role="option"
          @click="
            () => {
              selectCode(code);
            }
          "
        >
          <q-item-section>
            <q-item-label>
              {{ code.label }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>

      <div v-if="inputValue.length < 2" class="label help-text">
        Start typing to search clinical codes
      </div>
      <div v-if="isCodeMenuSearching === true" class="label searching-text">Searching codes...</div>
      <div
        v-if="
          isCodeMenuSearching === false && codeSearchResults.length < 1 && inputValue.length > 1
        "
        class="label no-results-text"
      >
        No results
      </div>
    </m-menu>
  </div>
</template>

<style lang="scss" scoped>
.consultation-entry-input {
  display: flex;
  flex: 1 1 auto;
}
textarea {
  flex: 1 1 auto;
  border: 0;
  width: 100%;
  resize: none;
  min-height: 26px;
  padding: 2px 8px;
  background: none;
  overflow: hidden;

  &::placeholder {
    font-style: italic;
    color: #777;
  }

  &:focus {
    outline: none;
  }
}

.label {
  padding: 14px;
  font-style: italic;
  color: var(--text-color-lightest);
}

.hidden-textarea {
  position: absolute;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  right: -9999px;
  width: 0;
  height: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
</style>
